import { ItemType, TrackingLink } from "./ResearchDataModel"
import { useResearchDataApi } from './ResearchDataApi'
import { AsyncData, LoadMode } from "@webapp/util/AsyncData"
import { AsyncDataStore, VersionedKeyList } from "@webapp/util/AsyncDataStore"
import { defineStore } from "pinia"
import { DateTime } from "luxon"
import { DateTimeUtil } from "@webapp/util/LuxonUtil"
import { assignExisting } from "@webapp/util/TypeScriptUtil"


export const useTrackingLinkStore = defineStore("trackingLinkStore", () => {
    // NOTE: only person links for now
    const asyncTrackingLinks = new Map<ItemType, Map<string, AsyncData<TrackingLink>>>([[ItemType.Person, new Map<string, AsyncData<TrackingLink>>()]])
    
    const rd = useResearchDataApi()

    function getAsyncTrackingLink(itemType: ItemType, itemId: string | undefined, loadMode = LoadMode.TrackOnly) {
        return getAsyncTrackingLinkList(itemType, [itemId], loadMode)[0]
    }
    
    function getAsyncTrackingLinkList(itemType: ItemType, itemIds: (string | undefined)[], loadMode = LoadMode.TrackOnly) {
        if (itemType != ItemType.Person)
            throw new Error(`${itemType} tracking links are not supported`)

        const store = useTrackingLinkStore() // use to get/return reactive store objects
        const itemTypePath = "persons"
        const loadAsync = async (idsToLoad: string[], requestTime: DateTime) => {
            const plainItems = await rd.getPlainByIdsAsync(id => `${itemTypePath}/${id}/tracking`, idsToLoad)
            console.log(`Loaded ${plainItems.length} tracking links by id`)
            return processExpandedTrackingLinks(plainItems, requestTime)
        }
        const storeMap = store.asyncTrackingLinks.get(itemType)!
        return AsyncDataStore.getAsyncItemsByIds(storeMap, itemIds, "TrackingLink", loadMode, loadAsync)
    }

    async function getTrackingLinksForTrackedItemAsync(itemType: ItemType, itemId: string | undefined) {
        if (itemType != ItemType.Person)
            throw new Error(`${itemType} tracking links are not supported`)

        const itemTypePath = "persons"
        const path = `${itemTypePath}/${itemId}/trackedby`
        const requestTime = DateTime.utc()

        const plainItems = await rd.getPlainCollectionAsync(path)
        console.log(`Loaded ${plainItems.length} tracking links at ${path}`)
        const itemMap = processExpandedTrackingLinks(plainItems, requestTime)
        const store = useTrackingLinkStore() // use to get/return reactive store objects
        const storeMap = store.asyncTrackingLinks.get(itemType)!
        AsyncDataStore.addItemMapToStore(storeMap, "TrackingLink", itemMap, requestTime)
        return [...itemMap.values()]
    }

    return {
        asyncTrackingLinks,

        getAsyncTrackingLink,
        getAsyncTrackingLinkList,
        getTrackingLinksForTrackedItemAsync,
    }
})

function plainToTrackingLink(p: any) {
    const t = assignExisting(new TrackingLink(), p)
    t.createdDate = DateTimeUtil.fromAPI(p.createdDate)
    return t;
}

function processExpandedTrackingLinks(plainLinks: any[], requestTime: DateTime) {
    return AsyncDataStore.createItemMap(plainLinks, "TrackingLink", plainToTrackingLink, t => t.itemId!)
}